import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, HeartHandshake, Loader2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1624272864537-8ecc72b67958?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxoYXBweSUyMHBhcmVudCUyMHBsYXlpbmclMjB3aXRoJTIwY2hpbGQlMjB3YXJtdGglMjB0cnVzdHxlbnwwfHx8fDE3ODgxMjExMTJ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "parent", child_name: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Welcome back, ${u.name}`);
      } else {
        const u = await register(form);
        toast.success(`Account created. Welcome, ${u.name}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden overflow-hidden bg-[#172554] lg:block">
        <img src={HERO} alt="Parent and child" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#172554] via-[#172554]/70 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0d9488]">
              <HeartHandshake className="h-5 w-5" />
            </div>
            <span className="font-display text-xl font-bold">Dual Care</span>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl font-bold leading-tight lg:text-5xl">
              Every visit matters. Every moment recorded with care.
            </h1>
            <p className="mt-5 text-base leading-relaxed text-slate-300">
              A trustworthy platform for scheduling supervised visits, tracking approvals and
              punctuality, and producing objective quality-of-visit reports.
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Shield className="h-4 w-4" />
            Court-admissible records &amp; audit trails
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1e3a8a]">
              <HeartHandshake className="h-5 w-5 text-white" />
            </div>
            <span className="font-display text-xl font-bold text-slate-900">Dual Care</span>
          </div>

          <h2 className="font-display text-2xl font-bold text-slate-900">
            {mode === "login" ? "Sign in to your portal" : "Create your account"}
          </h2>
          <p className="mt-1.5 text-sm text-slate-500">
            {mode === "login" ? "Access your visitation schedule and records." : "Join as a parent or social worker."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="register-name-input" value={form.name} onChange={set("name")} required placeholder="Jane Doe" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={form.email} onChange={set("email")} required placeholder="you@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password-input" value={form.password} onChange={set("password")} required placeholder="••••••••" />
            </div>

            {mode === "register" && (
              <>
                <div className="space-y-1.5">
                  <Label>I am a</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: "parent", l: "Parent" },
                      { v: "social_worker", l: "Social Worker" },
                    ].map((o) => (
                      <button
                        type="button"
                        key={o.v}
                        data-testid={`role-${o.v}`}
                        onClick={() => setForm({ ...form, role: o.v })}
                        className={`rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                          form.role === o.v
                            ? "border-[#1e3a8a] bg-[#1e3a8a] text-white"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
                {form.role === "parent" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="child">Child's name</Label>
                    <Input id="child" data-testid="register-child-input" value={form.child_name} onChange={set("child_name")} placeholder="Emma Doe" />
                  </div>
                )}
              </>
            )}

            <Button type="submit" data-testid="auth-submit-btn" disabled={loading} className="w-full bg-[#1e3a8a] py-6 text-base font-semibold hover:bg-[#172554]">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-semibold text-[#0d9488] hover:underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>

          <div className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
            <p className="font-semibold text-slate-600">Demo accounts</p>
            <p className="mt-1">Parent — parent@kindredbridge.com / parent123</p>
            <p>Social Worker — worker@kindredbridge.com / worker123</p>
          </div>
        </div>
      </div>
    </div>
  );
}
